import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Lightning, CheckCircle } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1559619081-8fd2cc754ca3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 6) { setErr("Password must be at least 6 characters."); return; }
    if (pw !== confirm) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative h-full flex flex-col justify-end p-12">
          <Lightning size={40} weight="fill" color="#FF007F" />
          <h2 className="font-display text-4xl font-black uppercase mt-4 leading-tight">Choose a<br />new password</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-black uppercase mb-1">Reset Password</h1>
          {done ? (
            <div data-testid="reset-done">
              <div className="flex items-center gap-2 text-[#39FF14] mt-6 mb-3">
                <CheckCircle size={22} weight="fill" />
                <span className="font-bold uppercase tracking-widest text-sm">Password updated!</span>
              </div>
              <p className="text-zinc-400 text-sm">You can now log in with your new password. Redirecting you to the login page…</p>
              <Link to="/login" className="inline-block mt-6 text-[#00FFFF] underline text-sm">Go to log in</Link>
            </div>
          ) : !token ? (
            <div data-testid="reset-no-token">
              <p className="text-[#FF007F] text-sm mt-6 border border-[#FF007F] p-3">This reset link is missing its token. Please request a new link.</p>
              <Link to="/forgot-password" className="inline-block mt-6 text-[#00FFFF] underline text-sm">Request a new link</Link>
            </div>
          ) : (
            <form onSubmit={submit}>
              <p className="text-zinc-400 text-sm mb-6">Enter a new password for your account.</p>
              {err && <p className="text-[#FF007F] text-sm mb-4 border border-[#FF007F] p-2" data-testid="reset-error">{err}</p>}
              <label className="text-xs uppercase tracking-widest text-zinc-400">New password</label>
              <input value={pw} onChange={(e) => setPw(e.target.value)} type="password" required data-testid="reset-password"
                className="w-full mt-1 mb-4 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white" />
              <label className="text-xs uppercase tracking-widest text-zinc-400">Confirm new password</label>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" required data-testid="reset-confirm"
                className="w-full mt-1 mb-6 bg-zinc-900 border border-zinc-700 focus:border-[#00FFFF] outline-none px-3 py-3 text-white" />
              <button type="submit" disabled={busy} data-testid="reset-submit"
                className="w-full py-3 bg-[#FF007F] text-white font-black uppercase tracking-widest border border-[#FF007F] hover:bg-[#09090B] hover:text-[#FF007F] transition-colors disabled:opacity-50">
                {busy ? "Updating…" : "Update Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
